import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { VideoUploader } from '../components/VideoUploader';
import { Results } from '../components/Results';
import { Sparkles, Target, Zap } from 'lucide-react';

export function Landing() {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [processingResult, setProcessingResult] = React.useState(null);

  const handleVideoSelect = (file: File, duration: number) => {
    setSelectedFile(file);
    setProcessingResult(null);
  };

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary to-purple-400 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"></div>
        </div>
        
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Transform Your Videos into Viral LinkedIn Posts
            </h1>
            <div className="mt-3 space-y-2">
              <p className="text-lg text-muted-foreground">
                AI-powered platform that turns your videos into engaging, high-converting LinkedIn content in seconds
              </p>
              <p className="text-lg text-muted-foreground">
                Trained on 1.2M viral LinkedIn posts
              </p>
            </div>
          </div>

          {/* Upload Tool */}
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="bg-card rounded-xl shadow-sm p-6 border">
              <VideoUploader
                onVideoSelect={handleVideoSelect}
                selectedFile={selectedFile}
              />
              {processingResult && (
                <div className="mt-8">
                  <Results {...processingResult} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-primary">Boost Your LinkedIn Presence</h2>
          <p className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            Everything you need to create viral LinkedIn content
          </p>
          <p className="mt-4 text-lg text-muted-foreground">
            Our AI-powered platform helps creators, businesses, and agencies create engaging LinkedIn content that drives results
          </p>
        </div>
        <div className="mx-auto mt-8 max-w-2xl lg:max-w-none">
          <dl className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
            <FeatureCard
              icon={<Sparkles className="h-6 w-6 text-primary" />}
              title="AI-Powered Captions"
              description="Our AI analyzes your video content and generates engaging captions optimized for LinkedIn's algorithm and your audience"
            />
            <FeatureCard
              icon={<Target className="h-6 w-6 text-primary" />}
              title="Targeted Engagement"
              description="Custom-tailored content that resonates with your specific industry and target audience"
            />
            <FeatureCard
              icon={<Zap className="h-6 w-6 text-primary" />}
              title="Instant Results"
              description="Generate professional LinkedIn posts in seconds, not hours. Focus on creating great video content"
            />
          </dl>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary">
        <div className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-primary-foreground">
              Ready to boost your LinkedIn engagement?
              <br className="hidden sm:block" />
              Start creating viral posts today
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-primary-foreground/90">
              Join thousands of creators and businesses who are already using our platform to generate high-performing LinkedIn content
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto px-6 py-3 text-base font-semibold rounded-lg bg-background text-foreground hover:bg-background/90"
              >
                Get started for free
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-base font-semibold text-primary-foreground hover:text-primary-foreground/90"
              >
                View pricing <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-card border-t">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Vertex Media Corporation. All rights reserved
            </p>
            <div className="flex space-x-6">
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex flex-col p-6 bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow border">
      <dt className="flex items-center gap-x-3 text-base font-semibold text-foreground">
        {icon}
        {title}
      </dt>
      <dd className="mt-4 flex flex-auto flex-col text-base text-muted-foreground">
        <p className="flex-auto">{description}</p>
      </dd>
    </div>
  );
}